import React from "react";
import { MessageCircle, Send, Camera, Globe } from "lucide-react";

/**
 * 📱 CUSTOM WHATSAPP-STYLE TOAST NOTIFICATION
 * Replicated WhatsApp notification design for new messages
 */
const CustomToast = ({
  customerName,
  message,
  channelType,
  timestamp,
  conversationId,
  onClose,
  onNavigate,
  toastId,
}) => {
  // Generate customer initials for avatar
  const getCustomerInitials = (name) => {
    if (!name || name === "Customer") return "C";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  // Get channel icon based on type
  const getChannelIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "whatsapp":
        return <MessageCircle className="w-4 h-4 text-green-600" />;
      case "telegram":
        return <Send className="w-4 h-4 text-blue-500" />;
      case "instagram":
        return <Camera className="w-4 h-4 text-pink-500" />;
      case "web":
      case "web chat":
        return <Globe className="w-4 h-4 text-blue-600" />;
      default:
        return <MessageCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return "now";
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Truncate message for preview
  const getMessagePreview = (msg) => {
    if (!msg) return "New message";
    if (msg.length <= 50) return msg;
    return msg.substring(0, 47) + "...";
  };

  // Handle toast click - navigate to conversation
  const handleToastClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log(`🍞 Toast clicked - attempting navigation...`);
    console.log(`🍞 ConversationId:`, conversationId);
    console.log(`🍞 OnNavigate function:`, !!onNavigate);
    
    if (onNavigate && conversationId) {
      console.log(`🍞 Toast clicked - navigating to conversation ${conversationId}`);
      onNavigate();
    } else {
      console.log(`🍞 Navigation not possible - conversationId: ${conversationId}, onNavigate: ${!!onNavigate}`);
    }
    
    // Close toast after attempting navigation
    if (onClose) {
      onClose();
    }
  };

  return (
    <div
      className="flex items-start gap-3 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-[320px] max-w-[400px] cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-750 select-none"
      onClick={handleToastClick}
      onMouseDown={(e) => e.preventDefault()}
      title={conversationId ? "Click to open conversation" : "Click to dismiss"}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToastClick(e);
        }
      }}
    >
      {/* Customer Avatar */}
      <div className="relative flex-shrink-0">
        <div className="w-12 h-12 bg-[#367BF4] rounded-full flex items-center justify-center">
          <span className="text-white font-semibold text-sm">
            {getCustomerInitials(customerName)}
          </span>
        </div>
        {/* Online indicator */}
        <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
      </div>

      {/* Message Content */}
      <div className="flex-1 min-w-0">
        {/* Header with name and channel */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold text-gray-900 dark:text-white text-sm truncate">
              {customerName || "Customer"}
            </h4>
            <span className="flex-shrink-0">{getChannelIcon(channelType)}</span>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
            {formatTime(timestamp)}
          </span>
        </div>

        {/* Message Preview */}
        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
          {getMessagePreview(message)}
        </p>

        {/* Action indicator */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {conversationId ? "Tap to open conversation" : "Tap to dismiss"}
          </span>
          <div className="flex items-center gap-1">
            {conversationId && (
              <span className="text-xs text-blue-500 dark:text-blue-400 font-medium">
                →
              </span>
            )}
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomToast;
